import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import DashboardCard from "../components/DashboardCard";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { api } from "common";
import { colors } from "components/Theme/WebTheme";
import { FONT_FAMILY } from "common/sharedFunctions";
import FranchiseSelector from "../components/FranchiseSelector";

import { DEFAULT_FRANCHISE_ID } from "../config/FranchisesConfig";

const Dashboard = () => {
  const [mylocation, setMylocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dailygross, setDailygross] = useState(0);
  const [monthlygross, setMonthlygross] = useState(0);
  const [totalgross, setTotalgross] = useState(0);
  const [settings, setSettings] = useState({});
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const usersdata = useSelector((state) => state.usersdata);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const cars = useSelector((state) => state.cartypes.cars);
  const [allCompleteCount, setAllCompleteCount] = useState([]);
  const [allCancelCount, setAllCancelCount] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [driverCount, setDriverCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [selectedFranchiseId, setSelectedFranchiseId] =
    useState(auth.profile.franchise_id || DEFAULT_FRANCHISE_ID);

  const isDefaultFranchiseUser =
    !auth.profile.franchise_id ||
    auth.profile.franchise_id === DEFAULT_FRANCHISE_ID;

  const filteredUsers = useMemo(() => {
    if (!usersdata.users) return [];

    return usersdata.users.filter((user) => {
      if (selectedFranchiseId === "all") {
        return true;
      }

      if (!user.franchise_id && selectedFranchiseId === DEFAULT_FRANCHISE_ID) {
        return true;
      }

      return user.franchise_id === selectedFranchiseId;
    });
  }, [usersdata.users, selectedFranchiseId]);

  const filteredBookings = useMemo(() => {
    if (!bookinglistdata.bookings) return [];


    return bookinglistdata.bookings.filter((booking) => {
      if (
        selectedFranchiseId === "all" ||
        (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
          !booking.franchise_id) ||
        booking.franchise_id === selectedFranchiseId
      ) {
        return true;
      }

      const user = usersdata.users?.find((u) => u.id === booking.customer_id);

      if (
        user &&
        !user.franchise_id &&
        selectedFranchiseId === DEFAULT_FRANCHISE_ID
      ) {
        return true;
      }

      return user && user.franchise_id === selectedFranchiseId;
    });
  }, [bookinglistdata.bookings, usersdata.users, selectedFranchiseId]);

  useEffect(() => {
    let totalEarnings = 0;
    let dailyEarnings = 0;
    let monthlyEarnings = 0;

    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);


    bookinglistdata?.bookings?.forEach((booking) => {
      if (booking.status === "PAID" || booking.status === "COMPLETE") {
        const bookingDate = new Date(booking.tripdate);

        if (
          selectedFranchiseId === "all" ||
          booking.franchise_id === selectedFranchiseId ||
          (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
            !booking.franchise_id)
        ) {
            console.log("booking", booking);
            console.log("booking.customer_paid", booking.customer_paid);
            console.log("totalEarnings", totalEarnings, typeof totalEarnings);
            console.log("booking.customer_paid", booking.customer_paid, typeof booking.customer_paid);
            totalEarnings += parseFloat(booking?.convenience_fees, 2);
        }

        if (bookingDate >= startOfDay) {
          if (
            selectedFranchiseId === "all" ||
            booking.franchise_id === selectedFranchiseId ||
            (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
              !booking.franchise_id)
          ) {
            dailyEarnings += parseFloat(booking.convenience_fees, 2);
          }
        }

        if (bookingDate >= startOfMonth) {
          if (
            selectedFranchiseId === "all" ||
            booking.franchise_id === selectedFranchiseId ||
            (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
              !booking.franchise_id)
          ) {
            monthlyEarnings += parseFloat(booking.convenience_fees, 2);
          }
        }
      }
    });

    setDailygross(dailyEarnings);
    setMonthlygross(monthlyEarnings);
    setTotalgross(totalEarnings);
  }, [bookinglistdata.bookings, usersdata.users, selectedFranchiseId]);

  useEffect(() => {
    const activeBookings = filteredBookings.filter((booking) =>
      ["NEW", "ACCEPTED", "ARRIVED", "STARTED", "REACHED"].includes(
        booking.status
      )
    );
    setActiveCount(activeBookings.length);
  }, [filteredBookings]);

  const renderFranchiseSelector = () => {
    if (DEFAULT_FRANCHISE_ID !== selectedFranchiseId) return null;

    return (
      <FranchiseSelector
        setSelectedFranchiseId={setSelectedFranchiseId}
        selectedFranchiseId={selectedFranchiseId}
        onChange={(e) => setSelectedFranchiseId(e.target.value)}
      />
    );
  };

  useEffect(() => {
    if (mylocation == null) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setMylocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }),
        (error) => console.log(error)
      );
    }
  }, [mylocation]);

  useEffect(() => {
    if (settingsdata.settings) {
      setSettings(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  useEffect(() => {
    if (usersdata.drivers && bookinglistdata.bookings) {
      const liveBookings = bookinglistdata.bookings.filter(
        (bkg) => bkg.status === "STARTED"
      );
      const drivers = usersdata.drivers;
      let locs = [];

      for (let i = 0; i < drivers.length; i++) {
        if (drivers[i].location) {
          let carImage =
            "https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png";
          let bookingRef = null;
          for (let j = 0; j < cars.length; j++) {
            if (cars[j].name === drivers[i].carType) {
              carImage = cars[j].image;
            }
          }
          for (let j = 0; j < liveBookings.length; j++) {
            if (liveBookings[j].driver === drivers[i].uid) {
              bookingRef = liveBookings[j].reference;
            }
          }
          locs.push({
            id: i,
            lat: drivers[i].location.lat,
            lng: drivers[i].location.lng,
            drivername: drivers[i].firstName + " " + drivers[i].lastName,
            carnumber: drivers[i].vehicleNumber,
            cartype: drivers[i].carType,
            carImage: carImage,
            bookingRef: bookingRef,
            fleetadmin: drivers[i].fleetadmin,
          });
        }
      }
      if (auth.profile.usertype === "fleetadmin") {
        locs = locs.filter((driver) => driver.fleetadmin === auth.profile.uid);
      }
      setLocations(locs);
    }
  }, [
    usersdata.drivers,
    auth.profile.usertype,
    auth.profile.uid,
    cars,
    bookinglistdata.bookings,
    settings,
  ]);

  const barChartOptions = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    colors: [colors.GREEN, colors.RED],
    options: {
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
      },
    },
    chart: {
      height: 350,
      type: "area",
      fontFamily: FONT_FAMILY,
    },
    xaxis: {
      labels: {
        style: {
          fontFamily: FONT_FAMILY,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontFamily: FONT_FAMILY,
        },
      },
    },
    title: {
      style: {
        fontFamily: FONT_FAMILY,
      },
    },
    subtitle: {
      style: {
        fontFamily: FONT_FAMILY,
      },
    },
    tooltip: {
      style: {
        fontFamily: FONT_FAMILY,
      },
    },
    legend: {
      fontFamily: FONT_FAMILY,
    },
  };

  const barChart = [
    {
      name: t("completed_bookings"),
      data: allCompleteCount,
    },
    {
      name: t("cancelled_bookings"),
      data: allCancelCount,
    },
  ];
  useEffect(() => {
    if (usersdata.users) {
      let driverCount = 0;
      let activeCount = 0;
      let customerCount = 0;
      for (let i = 0; i < usersdata.users.length; i++) {
        if (usersdata.users[i].usertype === "driver") {
          if (
            selectedFranchiseId === "all" ||
            usersdata.users[i].franchise_id === selectedFranchiseId ||
            (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
              !usersdata.users[i].franchise_id)
          ) {
            if (
              (auth.profile.usertype === "fleetadmin" &&
                usersdata.users[i].fleetadmin === auth.profile.uid) ||
              auth.profile.usertype === "admin"
            ) {
              driverCount = driverCount + 1;
            }
          }
        }
        if (usersdata.users[i].driverActiveStatus === true) {
          if (
            selectedFranchiseId === "all" ||
            usersdata.users[i].franchise_id === selectedFranchiseId ||
            (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
              !usersdata.users[i].franchise_id)
          ) {
            if (
              (auth.profile.usertype === "fleetadmin" &&
                usersdata.users[i].fleetadmin === auth.profile.uid) ||
              auth.profile.usertype === "admin"
            ) {
              activeCount = activeCount + 1;
            }
          }
        }
        if (usersdata.users[i].usertype === "customer") {
          if (
            selectedFranchiseId === "all" ||
            usersdata.users[i].franchise_id === selectedFranchiseId ||
            (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
              !usersdata.users[i].franchise_id)
          ) {
            if (
              (auth.profile.usertype === "fleetadmin" &&
                usersdata.users[i].fleetadmin === auth.profile.uid) ||
              auth.profile.usertype === "admin"
            ) {
              customerCount = customerCount + 1;
            }
          }
        }
      }
      setActiveCount(activeCount);
      setDriverCount(driverCount);
      setCustomerCount(customerCount);
    } else {
      setActiveCount(0);
      setDriverCount(0);
      setCustomerCount(0);
    }
  }, [usersdata.users, auth.profile, selectedFranchiseId]);

  useEffect(() => {
    let allCompleteCount = [];
    let allCancelCount = [];
    let months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    if (bookinglistdata.bookings) {
      for (let i = 0; i < months.length; i++) {
        let completeCount = 0;
        let cancelCount = 0;
        for (let j = 0; j < bookinglistdata.bookings.length; j++) {
          const { tripdate } = bookinglistdata.bookings[j];
          let tDate = new Date(tripdate);
          if (
            months[i] === months[tDate.getMonth()] &&
            (bookinglistdata.bookings[j].status === "PAID" ||
              bookinglistdata.bookings[j].status === "COMPLETE")
          ) {
            if (
              selectedFranchiseId === "all" ||
              bookinglistdata.bookings[j].franchise_id ===
                selectedFranchiseId ||
              (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
                !bookinglistdata.bookings[j].franchise_id)
            ) {
              completeCount = completeCount + 1;
            }
          }
          if (
            months[i] === months[tDate.getMonth()] &&
            bookinglistdata.bookings[j].status === "CANCELLED"
          ) {
            if (
              selectedFranchiseId === "all" ||
              bookinglistdata.bookings[j].franchise_id ===
                selectedFranchiseId ||
              (selectedFranchiseId === DEFAULT_FRANCHISE_ID &&
                !bookinglistdata.bookings[j].franchise_id)
            ) {
              cancelCount = cancelCount + 1;
            }
          }
        }
        allCompleteCount.push(completeCount);
        allCancelCount.push(cancelCount);
      }
    }
    setAllCompleteCount(allCompleteCount);
    setAllCancelCount(allCancelCount);
  }, [bookinglistdata.bookings, selectedFranchiseId]);

  return bookinglistdata.loading || usersdata.loading ? (
    <CircularLoading />
  ) : (
    <div style={{ position: "relative" }}>
      {renderFranchiseSelector()}
      <Typography
        variant="h4"
        style={{
          margin: "20px 20px 0 15px",
          textAlign: isRTL === "rtl" ? "right" : "left",
          fontFamily: FONT_FAMILY,
        }}
      >
        {t("gross_earning")}
      </Typography>
      <Grid container direction="row" spacing={2}>
        <Grid item xs style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}>
          {settings.swipe_symbol === false ? (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "19px",
                backgroundColor: colors.WALLET_CARD,
                color: colors.WHITE,
              }}
              title={t("today_text")}
              image={require("../assets/img/money1.jpg").default}
            >
              {settings.symbol + " " + dailygross}
            </DashboardCard>
          ) : (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "19px",
                backgroundColor: colors.WALLET_CARD,
                color: colors.WHITE,
              }}
              title={t("today_text")}
              image={require("../assets/img/money1.jpg").default}
            >
              {dailygross + " " + settings.symbol}
            </DashboardCard>
          )}
        </Grid>
        <Grid item xs style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}>
          {settings.swipe_symbol === false ? (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "19px",
                backgroundColor: colors.VIOLET,
                color: colors.WHITE,
              }}
              title={t("this_month_text")}
              image={require("../assets/img/money2.jpg").default}
            >
              {settings.symbol + " " + monthlygross}
            </DashboardCard>
          ) : (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "19px",
                backgroundColor: colors.VIOLET,
                color: colors.WHITE,
              }}
              title={t("this_month_text")}
              image={require("../assets/img/money2.jpg").default}
            >
              {monthlygross + " " + settings.symbol}
            </DashboardCard>
          )}
        </Grid>
        <Grid item xs style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}>
          {settings.swipe_symbol === false ? (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "19px",
                backgroundColor: colors.GREEN,
                color: colors.WHITE,
              }}
              title={t("total")}
              image={require("../assets/img/money3.jpg").default}
            >
              {settings.symbol + " " + totalgross.toFixed(2)}
            </DashboardCard>
          ) : (
            <DashboardCard
              crdStyle={{
                display: "flex",
                borderRadius: "19px",
                backgroundColor: colors.GREEN,
                color: colors.WHITE,
              }}
              title={t("total")}
              image={require("../assets/img/money3.jpg").default}
            >
              {totalgross + " " + settings.symbol}
            </DashboardCard>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Grid container style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <Grid
            item
            xs={12}
            sm={12}
            md={9}
            lg={9}
            style={{
              backgroundColor: colors.WHITE,
              borderRadius: 20,
              overflow: "hidden",
            }}
            boxShadow={3}
          >
            <Typography
              variant="h4"
              style={{
                margin: "20px 20px 0 15px",
                textAlign: isRTL === "rtl" ? "right" : "left",
                fontFamily: FONT_FAMILY,
              }}
            >
              {t("real_time_driver_section_text")}
            </Typography>
            {mylocation && mylocation.lat ? (
              <GoogleMap
                zoom={10}
                center={mylocation}
                mapContainerStyle={{ height: `380px`, fontFamily: FONT_FAMILY }}
              >
                {locations.map((marker) => (
                  <Marker
                    position={{ lat: marker.lat, lng: marker.lng }}
                    key={marker.id}
                    icon={{
                      url: marker.carImage,
                      scaledSize: new window.google.maps.Size(35, 25),
                    }}
                  >
                    <InfoWindow
                      position={{ lat: marker.lat, lng: marker.lng }}
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -32),
                      }}
                    >
                      <div style={{ fontFamily: FONT_FAMILY }}>
                        {marker.drivername}
                        <br />
                        {marker.carnumber}
                      </div>
                    </InfoWindow>
                  </Marker>
                ))}
              </GoogleMap>
            ) : null}
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            lg={3}
            style={{ padding: 25, marginTop: "30px" }}
          >
            <Card
              style={{
                borderRadius: "19px",
                backgroundColor: colors.WALLET_CARD,
                minHeight: 100,
                marginBottom: 20,
              }}
            >
              <Typography
                style={{
                  color: "white",
                  textAlign: "center",
                  fontSize: 20,
                  marginTop: 20,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {t("total_cumtomer")}
              </Typography>
              <Typography
                style={{
                  color: "white",
                  textAlign: "center",
                  fontSize: 26,
                  fontWeight: "bold",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {customerCount}
              </Typography>
            </Card>
            <Card
              style={{
                borderRadius: "19px",
                backgroundColor: colors.VIOLET,
                minHeight: 100,
                marginBottom: 20,
              }}
            >
              <Typography
                style={{
                  color: "white",
                  textAlign: "center",
                  fontSize: 20,
                  marginTop: 20,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {t("total_drivers")}
              </Typography>
              <Typography
                style={{
                  color: "white",
                  textAlign: "center",
                  fontSize: 26,
                  fontWeight: "bold",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {driverCount}
              </Typography>
            </Card>
            <Card
              style={{
                borderRadius: "19px",
                backgroundColor: colors.GREEN,
                minHeight: 100,
              }}
            >
              <Typography
                style={{
                  color: "white",
                  textAlign: "center",
                  fontSize: 20,
                  marginTop: 20,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {t("active_driver")}
              </Typography>
              <Typography
                style={{
                  color: "white",
                  textAlign: "center",
                  fontSize: 26,
                  fontWeight: "bold",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {activeCount}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Box
        style={{
          padding: 10,
          marginTop: 20,
          borderRadius: 20,
          backgroundColor: colors.WHITE,
        }}
        boxShadow={6}
      >
        <Typography
          style={{
            margin: "5px 20px 0 15px",
            color: colors.GREEN,
            fontWeight: "bold",
            fontFamily: FONT_FAMILY,
          }}
          variant="h5"
        >
          {t("booking_chart")}
        </Typography>
        <Chart
          options={barChartOptions}
          series={barChart}
          type="area"
          width="100%"
          height={360}
        />
      </Box>
    </div>
  );
};

export default Dashboard;
